import { decodeAudioDataCompat } from "./browserCompatibility";
import { SITE_TUNING } from "./siteTuning";

type RoomSound =
    | "playerJoin"
    | "hostControllerFound"
    | "hostControllerFoundReversed"
    | "gameModeStart"
    | "firstPlaceWinner"
    | "secondPlaceWinner"
    | "thirdPlaceWinner";

const SOUND_PATHS: Record<RoomSound, string> = {
    playerJoin: "/audio/lobby/player-join.mp3",
    hostControllerFound: "/audio/pictionary/host-controller-found.mp3",
    hostControllerFoundReversed: "/audio/pictionary/host-controller-found.mp3",
    gameModeStart: "/audio/lobby/game-mode-start.mp3",
    firstPlaceWinner: "/audio/results/first-place-winner.mp3",
    secondPlaceWinner: "/audio/results/second-place-winner.mp3",
    thirdPlaceWinner: "/audio/results/third-place-winner.mp3"
};

const SOUND_VOLUME: Record<RoomSound, number> = {
    playerJoin: SITE_TUNING.room.audio.playerJoinVolume,
    hostControllerFound: SITE_TUNING.room.audio.hostControllerFoundVolume,
    hostControllerFoundReversed: SITE_TUNING.room.audio.hostControllerLostVolume,
    gameModeStart: SITE_TUNING.room.audio.gameModeStartVolume,
    firstPlaceWinner: SITE_TUNING.room.audio.firstPlaceWinnerVolume,
    secondPlaceWinner: SITE_TUNING.room.audio.secondPlaceWinnerVolume,
    thirdPlaceWinner: SITE_TUNING.room.audio.thirdPlaceWinnerVolume
};

const REVERSED_SOUNDS = new Set<RoomSound>(["hostControllerFoundReversed"]);

const GLOBAL_SOUND_VOLUME_MULTIPLIER = SITE_TUNING.room.audio.globalVolumeMultiplier;
const MOBILE_SOUND_VOLUME_MULTIPLIER = SITE_TUNING.room.audio.mobileVolumeMultiplier;
const MOBILE_SOUND_SHORT_SIDE_MAX_PX = SITE_TUNING.room.audio.mobileVolumeShortSideMaxPx;

export class RoomAudio {
    private context: AudioContext | null = null;
    private readonly buffers = new Map<RoomSound, AudioBuffer>();
    private readonly loading = new Map<RoomSound, Promise<void>>();
    private readonly pendingSounds = new Set<RoomSound>();
    private disposed = false;
    private primed = false;
    private contextToken = 0;

    public async prepare(): Promise<void> {
        const context = await this.ensureContextReady();

        if (!context) {
            return;
        }

        await Promise.all(Object.keys(SOUND_PATHS).map((sound) => this.load(sound as RoomSound)));
        this.flushPendingSounds(context);
    }

    public playPlayerJoin() {
        this.play("playerJoin");
    }

    public playHostControllerFound() {
        this.play("hostControllerFound");
    }

    public playHostControllerLost() {
        this.play("hostControllerFoundReversed");
    }

    public playAccountSignIn() {
        this.play("hostControllerFound");
    }

    public playAccountSignOut() {
        this.play("hostControllerFoundReversed");
    }

    public playGameModeStart() {
        this.play("gameModeStart");
    }

    public playFirstPlaceWinner() {
        this.play("firstPlaceWinner");
    }

    public playSecondPlaceWinner() {
        this.play("secondPlaceWinner");
    }

    public playThirdPlaceWinner() {
        this.play("thirdPlaceWinner");
    }

    public dispose() {
        this.disposed = true;
        const context = this.context;
        this.resetContext(context, { clearBuffers: true });
        context?.close().catch(() => undefined);
    }

    private getContext(): AudioContext | null {
        if (this.disposed) {
            return null;
        }

        if (this.context?.state === "closed") {
            this.resetContext(this.context);
        }

        if (this.context) {
            return this.context;
        }

        const AudioContextConstructor =
            window.AudioContext ??
            (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext;

        if (!AudioContextConstructor) {
            return null;
        }

        const context = new AudioContextConstructor();
        this.context = context;
        this.contextToken += 1;
        return context;
    }

    private async ensureContextReady(): Promise<AudioContext | null> {
        const context = this.getContext();

        if (!context) {
            return null;
        }

        if (context.state !== "running") {
            await context.resume().catch(() => undefined);
        }

        if (context.state === "running") {
            this.primeContext(context);
        }

        if (context.state === "closed") {
            this.resetContext(context);
            return null;
        }

        return context;
    }

    private primeContext(context: AudioContext) {
        if (this.primed) {
            return;
        }

        const createBuffer = (context as AudioContext & { createBuffer?: AudioContext["createBuffer"] }).createBuffer;

        if (typeof createBuffer !== "function") {
            this.primed = true;
            return;
        }

        try {
            const buffer = createBuffer.call(context, 1, 1, Math.max(22050, context.sampleRate || 22050));
            const source = context.createBufferSource();
            const gain = context.createGain();

            gain.gain.value = 0;
            source.buffer = buffer;
            source.connect(gain);
            gain.connect(context.destination);
            source.start(0);
            source.onended = () => {
                source.disconnect();
                gain.disconnect();
            };
            this.primed = true;
        } catch {
            this.primed = true;
        }
    }

    private load(sound: RoomSound): Promise<void> {
        if (this.buffers.has(sound)) {
            return Promise.resolve();
        }

        const existingLoad = this.loading.get(sound);

        if (existingLoad) {
            return existingLoad;
        }

        const context = this.getContext();

        if (!context) {
            return Promise.resolve();
        }

        const contextToken = this.contextToken;
        let loadPromise: Promise<void>;
        loadPromise = fetch(SOUND_PATHS[sound])
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Unable to load ${sound} sound`);
                }

                return response.arrayBuffer();
            })
            .then((arrayBuffer) => {
                if (this.isContextStale(context, contextToken)) {
                    return null;
                }

                return decodeAudioDataCompat(context, arrayBuffer);
            })
            .then((buffer) => {
                if (!buffer || this.isContextStale(context, contextToken)) {
                    return;
                }

                this.buffers.set(
                    sound,
                    REVERSED_SOUNDS.has(sound) ? this.createReversedBuffer(context, buffer) : buffer
                );
            })
            .catch(() => undefined)
            .finally(() => {
                if (this.loading.get(sound) === loadPromise) {
                    this.loading.delete(sound);
                }
            });

        this.loading.set(sound, loadPromise);
        return loadPromise;
    }

    private play(sound: RoomSound) {
        void this.playWhenReady(sound);
    }

    private async playWhenReady(sound: RoomSound) {
        const context = await this.ensureContextReady();

        if (!context) {
            return;
        }

        if (!this.buffers.has(sound)) {
            await this.load(sound);
        }

        const buffer = this.buffers.get(sound);

        if (!buffer) {
            return;
        }

        if (context.state !== "running") {
            await context.resume().catch(() => undefined);
        }

        if (context.state !== "running" || this.disposed) {
            this.pendingSounds.add(sound);
            return;
        }

        this.startBufferedSound(context, sound, buffer);
    }

    private flushPendingSounds(context: AudioContext) {
        if (context.state !== "running" || this.disposed || this.pendingSounds.size === 0) {
            return;
        }

        const pendingSounds = Array.from(this.pendingSounds);
        this.pendingSounds.clear();

        for (const sound of pendingSounds) {
            const buffer = this.buffers.get(sound);

            if (!buffer) {
                this.pendingSounds.add(sound);
                continue;
            }

            this.startBufferedSound(context, sound, buffer);
        }
    }

    private startBufferedSound(context: AudioContext, sound: RoomSound, buffer: AudioBuffer) {
        const source = context.createBufferSource();
        const gain = context.createGain();

        source.buffer = buffer;
        gain.gain.value = this.getOutputVolume(sound);
        source.connect(gain);
        gain.connect(context.destination);
        source.onended = () => {
            source.disconnect();
            gain.disconnect();
        };
        source.start();
    }

    private createReversedBuffer(context: AudioContext, buffer: AudioBuffer): AudioBuffer {
        const reversedBuffer = context.createBuffer(buffer.numberOfChannels, buffer.length, buffer.sampleRate);

        for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
            const source = buffer.getChannelData(channel);
            const target = reversedBuffer.getChannelData(channel);

            for (
                let targetIndex = 0, sourceIndex = source.length - 1;
                targetIndex < source.length;
                targetIndex += 1, sourceIndex -= 1
            ) {
                target[targetIndex] = source[sourceIndex];
            }
        }

        return reversedBuffer;
    }

    private resetContext(context: AudioContext | null, options: { clearBuffers?: boolean } = {}) {
        if (context && this.context && context !== this.context) {
            return;
        }

        this.context = null;
        this.primed = false;
        this.loading.clear();

        if (options.clearBuffers) {
            this.buffers.clear();
        }

        if (options.clearBuffers) {
            this.pendingSounds.clear();
        }

        this.contextToken += 1;
    }

    private isContextStale(context: AudioContext, contextToken: number): boolean {
        if (this.disposed || this.context !== context || this.contextToken !== contextToken) {
            return true;
        }

        if (context.state === "closed") {
            this.resetContext(context);
            return true;
        }

        return false;
    }

    private getOutputVolume(sound: RoomSound): number {
        const baseVolume = SOUND_VOLUME[sound] * GLOBAL_SOUND_VOLUME_MULTIPLIER;

        if (!this.isPhoneSizedTouchDevice()) {
            return baseVolume;
        }

        return baseVolume * MOBILE_SOUND_VOLUME_MULTIPLIER;
    }

    private isPhoneSizedTouchDevice(): boolean {
        const shortSide = Math.min(window.innerWidth || Number.POSITIVE_INFINITY, window.innerHeight || Number.POSITIVE_INFINITY);
        const hasTouchPrimaryInput =
            navigator.maxTouchPoints > 0 ||
            window.matchMedia("(pointer: coarse)").matches ||
            window.matchMedia("(any-pointer: coarse)").matches;

        return hasTouchPrimaryInput && shortSide <= MOBILE_SOUND_SHORT_SIDE_MAX_PX;
    }
}
